import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { setToken, useAnalytics, parseToken } from "@piiqtechnologies/ui-lib";
import { useLocation, useNavigate } from "react-router-dom";
import { getRedirectPathAfterLogin, getUserContext, storeUserContext } from "./utils";
import * as Sentry from "@sentry/react";
import { PATHS } from "@constants/Paths";
export const AuthContext = createContext(null);
const AuthContextProvider = ({ children }) => {
    const [userContext, setUserContext] = useState(getUserContext());
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const analytics = useAnalytics();
    const navigate = useNavigate();
    const { state: locationState } = useLocation();
    const redirectPathAfterLogin = useMemo(() => getRedirectPathAfterLogin(userContext === null || userContext === void 0 ? void 0 : userContext.user), [userContext === null || userContext === void 0 ? void 0 : userContext.user]);
    const normalizeUserFromToken = (tokenPayload) => {
        if (!(tokenPayload === null || tokenPayload === void 0 ? void 0 : tokenPayload._id))
            return undefined;
        const organizations = tokenPayload.organizations || [];
        return {
            _id: tokenPayload._id,
            status: tokenPayload.status || "active",
            email: tokenPayload.email || "",
            name: tokenPayload.name || "",
            receiveEmailDigest: Boolean(tokenPayload.receiveEmailDigest),
            scanNotificationEmail: Boolean(tokenPayload.scanNotificationEmail),
            organization: organizations[0],
            organizations,
            communities: tokenPayload.communities,
            allow: tokenPayload.allow,
            deny: tokenPayload.deny,
            features: tokenPayload.features,
        };
    };
    const onLogin = (loggedInUser, sso) => {
        setShouldNavigate(true);
        const { token } = loggedInUser;
        const tokenPayload = parseToken(token);
        const user = normalizeUserFromToken(tokenPayload);
        setToken(token);
        setUserContext((_userContext) => {
            const newContext = Object.assign(Object.assign({}, _userContext), { sso, user: user || (_userContext === null || _userContext === void 0 ? void 0 : _userContext.user) });
            storeUserContext(newContext);
            return newContext;
        });
    };
    useEffect(() => {
        var _a, _b, _c;
        if ((_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a._id) {
            analytics.identify(userContext.user._id, {
                name: userContext.user.name,
                organization: (_b = userContext.user.organization) === null || _b === void 0 ? void 0 : _b.name,
                organizationId: (_c = userContext.user.organization) === null || _c === void 0 ? void 0 : _c._id,
            });
            Sentry.setUser({
                id: userContext.user._id,
            });
        }
    }, [userContext]);
    useEffect(() => {
        if (!(userContext === null || userContext === void 0 ? void 0 : userContext.user))
            return;
        let redirectPath = redirectPathAfterLogin;
        if (locationState) {
            redirectPath = PATHS.DASHBOARD;
        }
        shouldNavigate && navigate(redirectPath, { replace: true });
    }, [userContext === null || userContext === void 0 ? void 0 : userContext.user, redirectPathAfterLogin, locationState, navigate, shouldNavigate]);
    const onLogout = () => {
        setToken(null);
        sessionStorage.clear();
        setUserContext(null);
        Sentry.configureScope((scope) => {
            scope.setUser(null);
        });
    };
    return (React.createElement(AuthContext.Provider, { value: {
            user: userContext === null || userContext === void 0 ? void 0 : userContext.user,
            sso: userContext === null || userContext === void 0 ? void 0 : userContext.sso,
            onLogin,
            onLogout,
        } }, children));
};
export function useAuthContext() {
    return useContext(AuthContext);
}
export default AuthContextProvider;
